import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronLeft, MessageSquare, Lock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

// Types
interface ProfileItem {
  id: string;
  username: string;
  profileImageUrl?: string;
  postImageUrl?: string;
  followerCount?: number;
  fullName?: string;
  biography?: string;
  locked?: boolean; // 잠금 상태 필드
}

interface ProfileCarouselProps {
  profiles: ProfileItem[];
  onSelect: (profile: ProfileItem) => void; // 이름 변경: onProfileSelect → onSelect
  isLoading?: boolean;
  guestMode?: boolean; // 비로그인 모드 여부
  isLoadingPosts?: boolean; // 게시물 로딩 상태
  recentPosts?: {
    id: string;
    imagekit_url: string;
    caption?: string;
    likes_count?: number;
    timestamp?: string;
  }[]; // 최근 게시물 배열
}

// 게시물 로딩 Shimmer 컴포넌트
const PostShimmer = () => (
  <div className="flex gap-2 w-full mb-2">
    {Array(3)
      .fill(0)
      .map((_, i) => (
        <div
          key={i}
          className="relative aspect-square rounded-xl flex-1 bg-gradient-to-r from-background via-muted to-background"
          style={{
            backgroundSize: "200% 100%",
            animation: "shimmer 2s infinite linear",
          }}
        />
      ))}
  </div>
);

export function ProfileCarousel({
  profiles,
  onSelect,
  isLoading = false,
  guestMode = false,
  isLoadingPosts = false,
  recentPosts = [],
}: ProfileCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const wheelDeltaRef = useRef<number>(0);
  const WHEEL_SCROLL_THRESHOLD = 100;
  const navigate = useNavigate();
  const { toast } = useToast();

  // Motion values for parallax and interactive effects
  const motionX = useMotionValue(0);
  const rotateY = useTransform(motionX, [-300, 300], [15, -15]);
  const motionXSpring = useSpring(motionX, { damping: 20, stiffness: 200 });
  const rotateYSpring = useSpring(rotateY, { damping: 20, stiffness: 200 });

  const totalProfiles = profiles.length;

  // 화면 크기에 따라 표시할 항목 수 계산
  const [itemsToShow, setItemsToShow] = useState(5);
  const [visibleProfiles, setVisibleProfiles] = useState<ProfileItem[]>([]);

  // 반응형으로 항목 수 조정하는 함수
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    if (width < 640) {
      setItemsToShow(3); // 모바일
    } else if (width < 1024) {
      setItemsToShow(5); // 태블릿
    } else {
      setItemsToShow(7); // 데스크탑
    }
  }, []);

  // 프로필 업데이트 함수
  const updateVisibleProfiles = useCallback(() => {
    if (profiles.length === 0) {
      setVisibleProfiles([]);
      return;
    }
    
    const items: ProfileItem[] = [];
    const halfCount = Math.floor(itemsToShow / 2);
    
    for (let i = 0; i < itemsToShow; i++) {
      const offset = i - halfCount;
      const index = (currentIndex + offset + totalProfiles) % totalProfiles;
      if (profiles[index]) {
        items.push(profiles[index]);
      }
    }
    
    setVisibleProfiles(items);
  }, [currentIndex, profiles, itemsToShow, totalProfiles]);

  // 초기화 및 화면 크기 변경 감지
  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  // 프로필 목록이 변경되면 표시할 항목 업데이트
  useEffect(() => {
    updateVisibleProfiles();
  }, [profiles, currentIndex, itemsToShow, updateVisibleProfiles]);

  // 이전 프로필로 이동
  const handlePrev = useCallback(() => {
    if (totalProfiles === 0) return; // 프로필 없으면 동작 안 함
    setCurrentIndex((prevIndex) => (prevIndex - 1 + totalProfiles) % totalProfiles);
  }, [totalProfiles]);

  // 다음 프로필로 이동
  const handleNext = useCallback(() => {
    if (totalProfiles === 0) return; // 프로필 없으면 동작 안 함
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalProfiles);
  }, [totalProfiles]);

  // 드래그 제스처 핸들러
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setIsDragging(true);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setDragStartX(clientX);
    motionX.set(0);
  };

  const handleDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const deltaX = clientX - dragStartX;
    motionX.set(deltaX);
  };

  const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDragging) return;
    const clientX = 'touches' in e ? (e.changedTouches ? e.changedTouches[0].clientX : 0) : e.clientX;
    const deltaX = clientX - dragStartX;

    const threshold = 50; // 슬라이드 전환을 위한 임계값
    
    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        handlePrev();
      } else {
        handleNext();
      }
    }
    
    setIsDragging(false);
    motionX.set(0);
  };

  /**
   * Wheel scroll handler to navigate carousel horizontally with threshold
   */
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>): void => {
    e.preventDefault();
    const delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
    wheelDeltaRef.current += delta;
    if (wheelDeltaRef.current >= WHEEL_SCROLL_THRESHOLD) {
      handleNext();
      wheelDeltaRef.current = 0;
    } else if (wheelDeltaRef.current <= -WHEEL_SCROLL_THRESHOLD) {
      handlePrev();
      wheelDeltaRef.current = 0;
    }
  }, [handleNext, handlePrev]);

  /**
   * 프로필 카드 또는 중앙 카드의 '채팅 시작' 버튼 클릭 시 호출되는 함수.
   * 중앙 프로필인 경우 onSelect 콜백을 호출하거나,
   * 비로그인/잠금 상태에 따라 적절한 액션을 취함.
   * 중앙이 아닌 카드를 클릭하면 해당 카드를 중앙으로 이동시킴.
   */
  const handleProfileClick = (profile: ProfileItem, index: number) => {
    const centerIndex = Math.floor(itemsToShow / 2);

    // 비로그인 + 잠금 상태 프로필 선택 시 로그인 페이지로 이동
    if (guestMode && profile.locked) {
      toast({
        title: "로그인 필요",
        description: "이 계정과 대화하려면 로그인이 필요합니다.",
      });
      navigate('/auth', { state: { returnTo: '/instant-chat' } });
      return;
    }

    // onSelect는 모든 카드 클릭 시 호출
    onSelect(profile);

    // 중앙이 아닌 카드 클릭 시 캐러셀 중앙으로 이동
    if (index !== centerIndex) {
      const offset = index - centerIndex;
      setCurrentIndex((prev) => (prev + offset + totalProfiles) % totalProfiles);
    }
  };

  // 로딩 상태 UI - 고급 애니메이션 적용
  if (isLoading) {
    return (
      <motion.div 
        className="flex justify-center items-center py-10 h-[400px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className="flex flex-col items-center"
          initial={{ scale: 0.8 }}
          animate={{ 
            scale: [0.8, 1, 0.8],
            rotate: [0, 5, 0, -5, 0],
          }}
          transition={{
            scale: { repeat: Infinity, duration: 3 },
            rotate: { repeat: Infinity, duration: 5, ease: "easeInOut" }
          }}
        >
          <motion.div 
            className="h-24 w-24 bg-gradient-to-br from-primary/30 to-accent/30 rounded-full mb-4 flex items-center justify-center"
            animate={{
              boxShadow: [
                "0 0 0 0 rgba(139, 92, 246, 0.2)",
                "0 0 20px 10px rgba(139, 92, 246, 0.3)",
                "0 0 0 0 rgba(139, 92, 246, 0.2)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.div
              animate={{ rotateY: 360 }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary/80 via-white/20 to-accent/80 rounded-full opacity-90" />
            </motion.div>
          </motion.div>
          <motion.div
            className="h-4 w-32 bg-muted rounded mb-2"
            animate={{
              opacity: [0.4, 0.7, 0.4]
            }}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <motion.div
            className="h-3 w-24 bg-muted rounded"
            animate={{
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
          />
        </motion.div>
      </motion.div>
    );
  }

  // 데이터 없음 UI - 향상된 빈 상태 디자인
  if (profiles.length === 0 && !isLoading) {
    return (
      <motion.div 
        className="text-center py-10 h-[400px] flex items-center justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="max-w-md mx-auto">
          <motion.div 
            className="mb-6 rounded-full bg-muted/50 p-5 mx-auto w-20 h-20 flex items-center justify-center"
            whileHover={{ scale: 1.05, rotate: 5 }}
            transition={{ type: "spring", stiffness: 500, damping: 10 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="w-10 h-10 text-muted-foreground"
            >
              <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
              <circle cx="9" cy="9" r="2" />
              <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
            </svg>
          </motion.div>
          <h3 className="text-xl font-bold mb-2">표시할 프로필이 없습니다</h3>
          <p className="text-muted-foreground mb-4">검색이나 새로고침을 시도해보세요</p>
        </div>
      </motion.div>
    );
  }

  // 팔로워 수 포맷 함수 - 번호 형식 최적화
  const formatFollowerCount = (count?: number) => {
    if (count === undefined || count === null || isNaN(count)) return "정보 없음";
    if (count >= 1000000) return `${(count / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return count.toString();
  };

  return (
    <div className="w-full relative">
      {/* 방향 버튼 - 프로필이 있을 때만, 세련된 디자인으로 업그레이드 */}
      {totalProfiles > 1 && (
        <>
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.1, x: -2 }}
            whileTap={{ scale: 0.9 }}
            className="absolute left-0 lg:left-4 top-1/2 -translate-y-1/2 z-10"
          >
            <Button
              variant="outline"
              size="icon"
              onClick={handlePrev}
              className="h-10 w-10 rounded-full border border-border bg-background/80 shadow-md backdrop-blur-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="이전 프로필"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            whileHover={{ scale: 1.1, x: 2 }}
            whileTap={{ scale: 0.9 }}
            className="absolute right-0 lg:right-4 top-1/2 -translate-y-1/2 z-10"
          >
            <Button
              variant="outline"
              size="icon"
              onClick={handleNext}
              className="h-10 w-10 rounded-full border border-border bg-background/80 shadow-md backdrop-blur-sm hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
              aria-label="다음 프로필"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </motion.div>
        </>
      )}

      {/* 캐러셀 메인 컨테이너 - 3D 효과 및 드래그 기능 */}
      <motion.div
        ref={carouselRef}
        className="flex items-center justify-center overflow-hidden relative h-[400px] mx-auto"
        style={{ 
          perspective: "1500px",
          rotateY: rotateYSpring
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7 }}
        onMouseDown={handleDragStart}
        onTouchStart={handleDragStart}
        onMouseMove={handleDragMove}
        onTouchMove={handleDragMove}
        onMouseUp={handleDragEnd}
        onTouchEnd={handleDragEnd}
        onMouseLeave={() => isDragging && handleDragEnd}
        onWheel={handleWheel}
      >
        {/* 카드 목록 - 개선된 애니메이션과 움직임 */}
        <AnimatePresence initial={false}>
          {visibleProfiles.map((profile, index) => {
            // profile이 null이나 undefined가 아닌지 확인
            if (!profile) return null;

            const isCentered = index === Math.floor(itemsToShow / 2);
            const distanceFromCenter = index - Math.floor(itemsToShow / 2);
            const zIndex = 10 - Math.abs(distanceFromCenter);
            const isLocked = guestMode && profile.locked;
            const offsetX = distanceFromCenter * 120; // 카드 간 거리
            const offsetY = Math.abs(distanceFromCenter) * 20; // 중앙에서 멀어질수록 아래로 이동
            const scale = 1 - Math.abs(distanceFromCenter) * 0.15; // 중앙에서 멀어질수록 작아짐
            const opacity = isCentered ? 1 : (1 - Math.abs(distanceFromCenter) * 0.2);
            
            // 3D 회전 효과 - 중앙에서 멀어질수록 회전
            const rotateY = distanceFromCenter * 10;
            // 시차 효과 - 드래그에 따라 카드가 약간 움직임
            const dragOffset = isDragging ? motionXSpring.get() * (1 - Math.abs(distanceFromCenter) * 0.2) * 0.1 : 0;
            
            return (
              <motion.div
                key={`${profile.id}-${currentIndex}-${index}`}
                className="absolute cursor-pointer"
                initial={{ opacity: 0 }}
                animate={{ 
                  opacity,
                  x: offsetX + dragOffset,
                  y: offsetY,
                  scale,
                  rotateY,
                  z: -Math.abs(distanceFromCenter) * 50, // z축 위치, 중앙에서 멀어질수록 뒤로
                  filter: isLocked ? 'grayscale(50%) blur(2px)' : 'none'
                }}
                exit={{ opacity: 0 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  mass: 0.8
                }}
                style={{ 
                  zIndex,
                  transformStyle: "preserve-3d",
                  transformPerspective: 1000,
                }}
                whileHover={isCentered && !isLocked ? { 
                  scale: scale * 1.05,
                  transition: { duration: 0.3, type: "spring", stiffness: 300 }
                } : {}}
                onClick={() => handleProfileClick(profile, index)}
              >
                <motion.div
                  className={`
                    w-72 rounded-xl overflow-hidden shadow-xl border bg-card
                    relative transition-all
                    ${isCentered ? 'shadow-xl ring-2 ring-primary/50' : 'shadow-md opacity-80'}
                    ${isLocked ? 'border-gray-500/50' : ''}
                  `}
                  style={{ transformStyle: "preserve-3d", backfaceVisibility: "hidden" }}
                  whileHover={isCentered && !isLocked ? { 
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                  } : {}}
                >
                  {/* 포스트 이미지 - 향상된 시각 효과 */}
                  <div className="relative aspect-square overflow-hidden">
                    <motion.img
                      src={profile.postImageUrl || '/placeholder-post.svg'}
                      alt={profile.username ? `${profile.username}의 최근 게시물` : '게시물'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => (e.currentTarget.src = '/placeholder-post.svg')}
                      initial={{ scale: 1 }}
                      whileHover={{ scale: isCentered ? 1.05 : 1 }}
                      transition={{ duration: 0.5 }}
                    />
                    
                    {/* 이미지 오버레이 - 세련된 그래디언트 */}
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"
                      initial={{ opacity: 0.7 }}
                      whileHover={{ opacity: 0.9 }}
                      transition={{ duration: 0.3 }}
                    />
                    
                    {/* 잠금 오버레이 - 애니메이션 효과 */}
                    {isLocked && (
                      <motion.div 
                        className="absolute inset-0 flex items-center justify-center bg-black/50 backdrop-blur-md"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <motion.div 
                          className="rounded-full bg-white/20 p-3"
                          animate={{ 
                            scale: [1, 1.1, 1],
                            boxShadow: [
                              "0 0 0 rgba(255,255,255,0.4)",
                              "0 0 20px rgba(255,255,255,0.6)",
                              "0 0 0 rgba(255,255,255,0.4)"
                            ]
                          }}
                          transition={{ 
                            duration: 2,
                            repeat: Infinity,
                            repeatType: "loop"
                          }}
                        >
                          <Lock className="h-6 w-6 text-white" />
                        </motion.div>
                      </motion.div>
                    )}
                  </div>

                  {/* 프로필 정보 - 향상된 레이아웃과 효과 */}
                  <div className={`absolute bottom-0 left-0 right-0 p-3 text-white transition-opacity duration-300 ${isLocked ? 'opacity-0' : 'opacity-100'}`}>
                    <div className="flex items-start gap-2">
                      <Avatar className="h-10 w-10 border-2 border-white/30 flex-shrink-0">
                        <AvatarImage src={profile.profileImageUrl} alt={profile.username} />
                        <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-white">
                          {profile.username ? profile.username.charAt(0).toUpperCase() : '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 overflow-hidden">
                        <p className="font-bold truncate text-sm">@{profile.username || '알 수 없음'}</p>
                        <p className="text-xs text-white/90 truncate">
                          {formatFollowerCount(profile.followerCount)} 팔로워
                        </p>
                      </div>
                    </div>

                    {/* 중앙 카드 + 게시물 표시 */}
                    {isCentered && (
                      <div className="w-full mb-3">
                        {isLoadingPosts ? (
                          <PostShimmer />
                        ) : (
                          <div className="flex gap-2 w-full">
                            {recentPosts?.map((post) => (
                              <div
                                key={post.id}
                                className="relative aspect-square rounded-xl overflow-hidden shadow-md border transition-transform duration-500 hover:scale-[1.02] hover:shadow-lg flex-1"
                              >
                                {post.imagekit_url ? (
                                  <img
                                    src={post.imagekit_url}
                                    alt={`${profile.username || '사용자'}의 최근 게시물 이미지`}
                                    className="object-cover w-full h-full"
                                    loading="lazy"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-muted">
                                    <p className="text-xs text-muted-foreground">이미지 없음</p>
                                  </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300">
                                  <div className="absolute bottom-1 left-1 text-white text-xs">
                                    {post.likes_count ? `좋아요 ${post.likes_count}개` : ''}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 중앙 카드 + 잠금 해제 상태일 때만 채팅 버튼 표시 */}
                    {isCentered && !isLocked && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.3 }}
                      >
                        <Button
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation(); // 이벤트 버블링 방지
                            handleProfileClick(profile, index);
                          }}
                          className="w-full mt-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/40 text-white"
                          size="sm"
                        >
                          <MessageSquare className="mr-2 h-3.5 w-3.5" />
                          채팅 시작
                        </Button>
                      </motion.div>
                    )}

                    {/* 중앙 카드 + 잠금 상태일 때 로그인 유도 버튼 표시 */}
                    {isCentered && isLocked && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.3 }}
                      >
                        <Button
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            navigate('/auth', { state: { returnTo: '/instant-chat' } });
                          }}
                          className="w-full mt-3 bg-white/20 backdrop-blur-sm hover:bg-white/30 border border-white/40 text-white"
                          size="sm"
                          disabled={!guestMode}
                          aria-label={`@${profile.username} 프로필 확인을 위해 로그인`}
                        >
                          <Lock className="mr-2 h-3.5 w-3.5" />
                          로그인하여 확인
                        </Button>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </motion.div>

      {/* 페이지 인디케이터 - 애니메이션과 인터랙션 개선 */}
      <motion.div 
        className="flex justify-center pb-6 mt-3 gap-1.5"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        {profiles.length > 1 && profiles.map((profile, idx) => {
          const isActive = idx === currentIndex;
          const isLocked = guestMode && (profile?.locked ?? false);
          
          return (
            <motion.button
              key={idx}
              className={`h-2 rounded-full transition-all ${
                isActive
                  ? isLocked 
                    ? 'bg-gray-500 w-6' 
                    : 'bg-primary w-6 shadow-lg shadow-primary/30' 
                  : isLocked 
                    ? 'bg-gray-500/50 hover:bg-gray-500/80 w-2' 
                    : 'bg-muted hover:bg-muted-foreground w-2'
              }`}
              onClick={() => setCurrentIndex(idx)}
              aria-label={`프로필 ${idx + 1} 보기`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
              animate={isActive ? { 
                scale: [1, 1.1, 1],
                transition: { 
                  duration: 1, 
                  repeat: Infinity,
                  repeatType: "reverse" 
                }
              } : {}}
            />
          );
        })}
      </motion.div>

      {/* 비로그인 사용자 안내 메시지 - 디자인 개선 및 애니메이션 */}
      {guestMode && (
        <motion.div 
          className="mt-4 text-center mb-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
            <Lock className="inline-block h-3 w-3 mr-1 mb-0.5" />
            <span>잠금 표시된 계정은 로그인 후 이용 가능합니다</span>
          </p>
        </motion.div>
      )}
    </div>
  );
}